import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

interface ScheduleInfo {
  client: string;
  mailingNumber: number;
}

interface RescheduleModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedClients: string[], selectedMailings: number[]) => void;
  loading: boolean;
  /** Array of objects describing each scheduled item */
  scheduledItems: ScheduleInfo[];
  /** Map of client codes to display names */
  clientNames: Record<string, string>;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  scheduledItems,
  clientNames,
}) => {
  // Get unique clients and mailings that have scheduled items
  const availableClients = useMemo(
    () => Array.from(new Set(scheduledItems.map((s) => s.client))).sort(),
    [scheduledItems],
  );

  const availableMailings = useMemo(
    () => Array.from(new Set(scheduledItems.map((s) => s.mailingNumber))).sort((a, b) => a - b),
    [scheduledItems],
  );

  // State for selections - default to all selected
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedMailings, setSelectedMailings] = useState<number[]>([]);

  // Reset selections when modal opens
  useEffect(() => {
    if (open) {
      setSelectedClients(availableClients);
      setSelectedMailings(availableMailings);
    }
  }, [open, availableClients, availableMailings]);

  // Calculate how many items will be affected
  const affectedCount = useMemo(() => {
    return scheduledItems.filter(
      (s) => selectedClients.includes(s.client) && selectedMailings.includes(s.mailingNumber),
    ).length;
  }, [scheduledItems, selectedClients, selectedMailings]);

  // Handlers for "Alle" checkboxes
  const allClientsSelected = selectedClients.length === availableClients.length;
  const allMailingsSelected = selectedMailings.length === availableMailings.length;

  const toggleAllClients = () => {
    setSelectedClients(allClientsSelected ? [] : availableClients);
  };

  const toggleAllMailings = () => {
    setSelectedMailings(allMailingsSelected ? [] : availableMailings);
  };

  const toggleClient = (client: string) => {
    setSelectedClients((prev) =>
      prev.includes(client) ? prev.filter((c) => c !== client) : [...prev, client],
    );
  };

  const toggleMailing = (mailing: number) => {
    setSelectedMailings((prev) =>
      prev.includes(mailing) ? prev.filter((m) => m !== mailing) : [...prev, mailing],
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedClients, selectedMailings);
  };

  const canConfirm = selectedClients.length > 0 && selectedMailings.length > 0 && affectedCount > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon color="warning" />
          Inhalte aktualisieren
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Wählen Sie aus, welche Clients und Mailings aktualisiert werden sollen.
        </Alert>

        {/* Client Selection */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Clients auswählen:
        </Typography>
        <FormGroup row sx={{ mb: 2, ml: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allClientsSelected}
                indeterminate={selectedClients.length > 0 && !allClientsSelected}
                onChange={toggleAllClients}
              />
            }
            label="Alle"
          />
          {availableClients.map((client) => (
            <FormControlLabel
              key={client}
              control={
                <Checkbox
                  checked={selectedClients.includes(client)}
                  onChange={() => toggleClient(client)}
                />
              }
              label={clientNames[client] || client.toUpperCase()}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Mailing Selection */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Mailings auswählen:
        </Typography>
        <FormGroup row sx={{ mb: 2, ml: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allMailingsSelected}
                indeterminate={selectedMailings.length > 0 && !allMailingsSelected}
                onChange={toggleAllMailings}
              />
            }
            label="Alle"
          />
          {availableMailings.map((mailing) => (
            <FormControlLabel
              key={mailing}
              control={
                <Checkbox
                  checked={selectedMailings.includes(mailing)}
                  onChange={() => toggleMailing(mailing)}
                />
              }
              label={`Mailing ${mailing}`}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Preview Count */}
        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">
            <strong>{affectedCount}</strong> Sendung{affectedCount !== 1 ? 'en' : ''}
            {affectedCount !== 1 ? ' werden' : ' wird'} aktualisiert
          </Typography>
        </Box>

        {/* Info about ESP behavior */}
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" component="div">
            <strong>Hinweise:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Mailchimp (BE, BG, SW, TT): Inhalt wird direkt aktualisiert</li>
              <li>Iterable (FT): Kampagne wird storniert und neu erstellt</li>
              <li>Bereits versendete E-Mails bleiben unverändert</li>
            </ul>
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={!canConfirm || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {loading ? 'Aktualisiere...' : 'Jetzt aktualisieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleModal;
